import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ChevronRight } from 'lucide-react';
import axiosInstance from '../api/axios';
import SaleCountdown from './SaleCountdown';
import { imgVariant } from '../utils/imgUrl';

/**
 * Eye-catching SALE-Banner für Homepage und Shop.
 * Lädt die Sale-Übersicht und rendert NUR wenn es aktuell reduzierte
 * Artikel gibt. So entsteht kein toter Banner-Slot in „normalen" Zeiten.
 *
 * Props:
 *   compact: boolean — kompaktere Variante (für Shop-Seite oben)
 */
const SaleBannerSection = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get('/products/sale')
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || !data || !data.total) return null;

  const maxDiscount = (data.categories || []).reduce(
    (m, c) => Math.max(m, c.max_discount_pct || 0), 0
  );
  // Bis zu 3 Beispiel-Artikel über alle Kategorien
  const previews = [];
  for (const cat of data.categories || []) {
    for (const sub of cat.subcategories || []) {
      for (const it of sub.items || []) {
        if (previews.length < 3) previews.push(it);
      }
    }
  }

  if (compact) {
    return (
      <Link
        to="/sale"
        className="block relative overflow-hidden mb-4 group"
        data-testid="sale-banner-compact"
      >
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#B0512E] via-[#A04A2A] to-[#8B3D24] text-white transition-transform group-hover:scale-[1.01]">
          <div className="p-2 bg-white/15 backdrop-blur-sm rounded-full">
            <Flame className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm sm:text-base" style={{ fontFamily: 'Georgia, serif' }}>
              SALE jetzt im Shop
              {maxDiscount > 0 && <span className="ml-2 text-[#FFE4A3]">−bis {maxDiscount}%</span>}
            </p>
            <p className="text-[11px] sm:text-xs text-white/80">
              {data.total} reduzierte Artikel · alle Kategorien automatisch sortiert
            </p>
            {data.next_ends_at && (
              <SaleCountdown
                targetIso={data.next_ends_at}
                className="text-white/90 mt-0.5"
                prefix="Endet in"
              />
            )}
          </div>
          <ChevronRight className="h-5 w-5 shrink-0" />
        </div>
      </Link>
    );
  }

  return (
    <section className="py-6 md:py-10 px-3 sm:px-6 lg:px-8" data-testid="sale-banner-home">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#B0512E] via-[#A04A2A] to-[#8B3D24] text-white">
          {/* Decorative pattern */}
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.4), transparent 35%), radial-gradient(circle at 82% 78%, rgba(255,255,255,0.35), transparent 38%)',
            }}
          />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-center p-5 md:p-8">
            {/* Left: text */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-2 py-1 bg-white/15 backdrop-blur-sm text-[10px] tracking-[4px] uppercase mb-3">
                <Flame className="h-3 w-3" /> Limited Sale
              </div>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2"
                style={{ fontFamily: 'Georgia, serif', letterSpacing: '-1px' }}
              >
                SALE
                {maxDiscount > 0 && (
                  <span className="ml-3 text-2xl sm:text-3xl md:text-4xl text-[#FFE4A3]">
                    bis −{maxDiscount}%
                  </span>
                )}
              </h2>
              <p className="text-sm sm:text-base text-white/90 mb-4 max-w-md">
                {data.total} reduzierte Artikel aus {data.categories.length}{' '}
                {data.categories.length === 1 ? 'Kategorie' : 'Kategorien'} —
                {' '}automatisch sortiert, jederzeit aktuell.
              </p>
              {data.next_ends_at && (
                <div className="mb-4 inline-block px-3 py-1.5 bg-black/20 backdrop-blur-sm">
                  <SaleCountdown
                    targetIso={data.next_ends_at}
                    className="text-white"
                    prefix="🔥 Aktion endet in"
                  />
                </div>
              )}
              <Link
                to="/sale"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#B0512E] font-bold tracking-wider hover:bg-[#FFE4A3] transition-colors text-sm"
                data-testid="sale-banner-cta"
              >
                Zum SALE-Bereich
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Right: preview thumbnails */}
            {previews.length > 0 && (
              <div className="hidden sm:flex gap-2 justify-end">
                {previews.map((p) => (
                  <Link
                    key={p.id}
                    to={`/produkt/${p.id}`}
                    className="relative w-24 h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 border-2 border-white/30 hover:border-white overflow-hidden bg-white/10"
                    title={p.name}
                  >
                    {p.images?.[0] && (
                      <img
                        src={imgVariant(p.images[0], { w: 400 })}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    {p.discount_pct > 0 && (
                      <span className="absolute top-0 left-0 px-1.5 py-0.5 bg-white text-[#B0512E] text-[10px] font-bold">
                        −{p.discount_pct}%
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SaleBannerSection;
